
import React from 'react';
import { Star } from 'lucide-react';

const renderElement = (element: any, idx: number) => {
    const { type, content, style } = element;
    const commonStyle = {
        ...style?.spacing,
        ...style?.typography,
        color: style?.colors?.text,
        backgroundColor: style?.colors?.background,
        borderColor: style?.borders?.color,
        borderRadius: style?.borders?.radius,
    };

    switch (type) {
        case 'heading':
            const Tag = (content.level || 'h2') as React.ElementType;
            return <Tag key={idx} className={`font-black uppercase tracking-tight ${content.size || 'text-3xl'} mb-4`} style={commonStyle}>{content.text}</Tag>;
        case 'text':
            return <div key={idx} className="prose prose-slate max-w-none text-slate-600 leading-relaxed font-medium" style={commonStyle}>{content.text}</div>;
        case 'rich_text':
            return <div key={idx} className="prose prose-slate max-w-none text-slate-600 leading-relaxed" style={commonStyle} dangerouslySetInnerHTML={{ __html: content.text }} />;
        case 'icon_list':
            return (
                <ul key={idx} className="space-y-3" style={commonStyle}>
                    {content.items?.map((item: any, i: number) => (
                        <li key={i} className="flex items-start gap-3">
                            <span className="text-indigo-500 mt-1"><Star size={16} /></span>
                            <span className="text-slate-700 font-medium">{item.text}</span>
                        </li>
                    ))}
                </ul>
            );
        case 'button':
            return (
                <a key={idx} href={content.link} className={`inline-flex items-center gap-2 px-8 py-3 rounded-lg font-black text-xs uppercase tracking-widest transition-all hover:scale-105 ${content.variant === 'outline' ? 'border-2 border-slate-900 text-slate-900' : 'bg-slate-900 text-white'}`} style={commonStyle}>
                    {content.label}
                </a>
            );
        case 'image':
            return <img key={idx} src={content.url} alt={content.alt} className="w-full h-auto rounded-xl shadow-sm object-cover" style={commonStyle} />;
        case 'icon':
            return <div key={idx} className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-900 mb-4"><Star size={24} /></div>;
        case 'divider':
            return <hr key={idx} className="my-8 border-slate-200" style={commonStyle} />;
        case 'spacer':
            return <div key={idx} style={{ height: content.height || '20px' }} />;
        case 'card':
            return (
                <div key={idx} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow" style={commonStyle}>
                    {content.title && <h4 className="font-bold text-lg mb-2">{content.title}</h4>}
                    {content.text && <p className="text-sm text-slate-600 mb-4">{content.text}</p>}
                    {content.link && <a href={content.link} className="text-xs font-black uppercase tracking-widest text-[#e51b23]">Lees meer</a>}
                </div>
            );
        case 'stats_counter':
            return (
                <div key={idx} className="text-center">
                    <div className="text-4xl font-black text-slate-900 mb-1">{content.value}</div>
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">{content.label}</div>
                </div>
            );
        case 'vacancy_list':
            return <div key={idx} className="p-4 bg-slate-50 border border-dashed border-slate-300 rounded text-center text-slate-400 text-xs font-bold uppercase">Dynamic Vacancy List (Preview)</div>;
        case 'logo_cloud':
            return (
                <div key={idx} className="flex flex-wrap items-center justify-center gap-8 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
                    {content.items?.map((item: any, i: number) => (
                        <img key={i} src={item.url} alt={item.alt} className="h-8 object-contain" />
                    ))}
                </div>
            );
        default:
            return <div key={idx} className="text-red-500 text-xs">Unknown widget: {type}</div>;
    }
};

const SectionRenderer: React.FC<{ section: any }> = ({ section }) => {
    const { layout, styles, elements } = section;
    const containerClass = layout?.containerType === 'full-width' ? 'w-full' : 'max-w-7xl mx-auto px-6';
    const columns = layout?.columns || 1;
    const alignment = layout?.verticalAlignment === 'middle' ? 'items-center' : layout?.verticalAlignment === 'bottom' ? 'items-end' : 'items-start';

    // Gap Mapping
    const gapMap: Record<string, string> = {
        'none': 'gap-0',
        'small': 'gap-4 md:gap-6',
        'medium': 'gap-8 md:gap-12',
        'large': 'gap-12 md:gap-20'
    };
    const gapClass = gapMap[layout?.gap || 'medium'] || 'gap-8 md:gap-12';

    const columnContent: any[][] = Array.from({ length: columns }, () => []);
    if (elements) {
        elements.forEach((el: any) => {
            const colIdx = el.columnIndex !== undefined && el.columnIndex < columns ? el.columnIndex : 0;
            columnContent[colIdx].push(el);
        });
    }

    return (
        <section
            className={`overflow-hidden relative ${styles?.padding || 'py-20'} ${styles?.fontFamily || 'font-sans'} ${styles?.fontSize || 'text-base'} ${styles?.textShadow || ''}`}
            style={{
                backgroundColor: styles?.backgroundColor,
                color: styles?.textColor,
                backgroundImage: styles?.backgroundImage ? `url(${styles?.backgroundImage})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className={containerClass}>
                <div className={`grid grid-cols-1 md:grid-cols-${columns} ${gapClass} ${alignment}`}>
                    {columnContent.map((colElements, colIdx) => (
                        <div key={colIdx} className="space-y-6 flex flex-col h-full">
                            {colElements.map((el, itemIdx) => renderElement(el, itemIdx))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default SectionRenderer;
